import React from 'react';
import './DemandCoveragePopup.css';

const DemandCoveragePopup = ({ data, onClose }) => {
  const { stock, cbp, bagsAvailable, seedsAvailable, seedsInTreatment, seedsUnderGermination, gperpacket, demands } = data;
  const periods = [0, 7, 14, 30, 90, 180, 365];

  // Total Coverage: Stock + CBP
  const totalAvailable = stock + cbp;
  const totalCoverage = periods.map((period, index) => {
    const demand = period === 0 ? 0 : demands[index - 1] || 0;
    const remainingStock = period === 0 ? totalAvailable : totalAvailable - demand;
    return {
      period,
      demand,
      covers: period === 0 ? true : totalAvailable >= demand,
      stock: index === 0 ? totalAvailable : '-', // Show stock only in first row
      remainingStock: remainingStock.toLocaleString(), // Allow negative values
    };
  });

  // Bags Coverage: Only packets from bags
  const packetsFromBags = bagsAvailable; // 1 bag = 1 packet
  const bagsCoverage = periods.map((period, index) => {
    const demand = period === 0 ? 0 : demands[index - 1] || 0;
    const remainingStock = period === 0 ? packetsFromBags : packetsFromBags - demand;
    return {
      period,
      demand,
      covers: period === 0 ? true : packetsFromBags >= demand, // Only packetsFromBags, no stock
      stock: index === 0 ? packetsFromBags : '-', // Show empty packets stock only in first row
      remainingStock: remainingStock.toLocaleString(), // Allow negative values
    };
  });

  // Seeds Coverage: Only packets from seeds
  const packetsFromSeeds = seedsAvailable && gperpacket ? Math.floor(seedsAvailable * 453.592 / gperpacket) : 0;
  const seedsStockLb = Number(seedsAvailable);
  const seedsStockg = Number(seedsAvailable * 453.592);
  const seedsInTreatmentLb = Number(seedsInTreatment);
  const seedsUnderGerminationLb = Number(seedsUnderGermination);
  const seedsCoverage = periods.map((period, index) => {
    const demand = period === 0 ? 0 : demands[index - 1] || 0;
    const seedsRequiredLb = gperpacket ? (demand * gperpacket / 453.592).toFixed(2) : 'N/A';
    const seedsRemainingLb = period === 0 ? seedsStockLb : gperpacket ? (seedsStockLb - (demand * gperpacket / 453.592)).toFixed(2) : 'N/A';
    return {
      period,
      demand,
      covers: period === 0 ? true : packetsFromSeeds >= demand, // Only packetsFromSeeds, no stock
      stock: index === 0 ? `${seedsStockLb} lb` : '-', // Show seeds stock in pounds only in first row
      seedsRequiredLb,
      seedsRemainingLb: period === 0 
        ? `${seedsRemainingLb}/${seedsInTreatmentLb.toLocaleString()}/${seedsUnderGerminationLb.toLocaleString()}` 
        : `${seedsRemainingLb}`
    };
  });

  return (
    <div className="demand-coverage-popup-overlay">
      <div className="demand-coverage-popup-content">
        <button className="demand-coverage-popup-close-button" onClick={onClose}>
          ×
        </button>
        <h2>Details for SKU: {data.sku}</h2>

        {/* Total Coverage Table */}
        <h3>Total (Stock + CBP)</h3>
        <table className="demand-coverage-popup-table">
          <thead>
            <tr>
              <th>Period (days)</th>
              <th>Stock</th>
              <th>Demand</th>
              <th>Remaining Stock</th>
              <th>Covers</th>
            </tr>
          </thead>
          <tbody>
            {totalCoverage.map((item, index) => (
              <tr key={index}>
                <td>{item.period}</td>
                <td>{typeof item.stock === 'number' ? item.stock.toLocaleString() : item.stock}</td>
                <td>{item.demand.toLocaleString()}</td>
                <td>{item.remainingStock}</td>
                <td className={item.covers ? 'demand-coverage-popup-green' : 'demand-coverage-popup-red'}>
                  {item.covers ? 'Yes' : 'No'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Bags and Seeds Coverage Tables Side by Side */}
        <div className="demand-coverage-popup-table-row">
          <div className="demand-coverage-popup-table-column">
            <h3>Empty Packets</h3>
            <table className="demand-coverage-popup-table">
              <thead>
                <tr>
                  <th>Period (days)</th>
                  <th>Stock</th>
                  <th>Demand</th>
                  <th>Remaining Stock</th>
                  <th>Covers</th>
                </tr>
              </thead>
              <tbody>
                {bagsCoverage.map((item, index) => (
                  <tr key={index}>
                    <td>{item.period}</td>
                    <td>{typeof item.stock === 'number' ? item.stock.toLocaleString() : item.stock}</td>
                    <td>{item.demand.toLocaleString()}</td>
                    <td>{item.remainingStock}</td>
                    <td className={item.covers ? 'demand-coverage-popup-green' : 'demand-coverage-popup-red'}>
                      {item.covers ? 'Yes' : 'No'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="demand-coverage-popup-table-column">
            <h3>Seeds</h3>
            <table className="demand-coverage-popup-table">
              <thead>
                <tr>
                  <th>Period (days)</th>
                  <th>Stock</th>
                  <th>Demand</th>
                  <th>Seeds Required (lb)</th>
                  <th>Seeds Remaining (lb)</th>
                  <th>Covers</th>
                </tr>
              </thead>
              <tbody>
                {seedsCoverage.map((item, index) => (
                  <tr key={index}>
                    <td>{item.period}</td>
                    <td>{item.stock}</td>
                    <td>{item.demand.toLocaleString()}</td>
                    <td>{item.seedsRequiredLb}</td>
                    <td>
                      {item.seedsRemainingLb.includes('/') ? (
                        <>
                          {item.seedsRemainingLb.split('/')[0]}
                          <span style={{ color: 'gray' }}>
                            /{item.seedsRemainingLb.split('/')[1]}/{item.seedsRemainingLb.split('/')[2]}
                          </span>
                        </>
                      ) : (
                        item.seedsRemainingLb
                      )}
                    </td>
                    <td className={item.covers ? 'demand-coverage-popup-green' : 'demand-coverage-popup-red'}>
                      {item.covers ? 'Yes' : 'No'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Calculation Log */}
        <div className="demand-coverage-popup-log">
          <h4>Calculation Log</h4>
          <p>Stock Packets with Seeds: {stock.toLocaleString()} packets</p>
          <p>CBP: {cbp.toLocaleString()} packets</p>
          <p>Total Available (Stock + CBP): {totalAvailable.toLocaleString()} packets</p>
          <br></br>
          <h4>CBP Details:</h4>
          <p>Empty physical Packets Available: {bagsAvailable.toLocaleString()}</p>
          <p>Seeds Available LB: {seedsStockLb.toLocaleString()} lb</p>
          <p>Seeds In Treatment LB: <span style={{ color: 'gray' }}>{seedsInTreatmentLb.toLocaleString()} lb</span></p>
          <p>Seeds Under Germination Test LB: <span style={{ color: 'gray' }}>{seedsUnderGerminationLb.toLocaleString()} lb</span></p>
          <p>Seeds Available G: {seedsStockg.toLocaleString()} g</p>
          <p>Grams per Packet: {gperpacket ? gperpacket.toLocaleString() : 'N/A'} g</p>
          <p>Packets from Seeds: {seedsAvailable && gperpacket ? `(${seedsStockLb} lb × 453.592 g/lb) ÷ ${gperpacket} g/packet = ${packetsFromSeeds.toLocaleString()} packets` : '0 packets'}</p>
        </div>

        <button className="demand-coverage-popup-button" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default DemandCoveragePopup;